
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, Mail, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface EmailConfirmationScreenProps {
  email: string;
}

export const EmailConfirmationScreen = ({ email }: EmailConfirmationScreenProps) => {
  const { t } = useTranslation('auth');
  const [isResending, setIsResending] = useState(false);

  const handleResendEmail = async () => {
    setIsResending(true);
    try {
      console.log('Attempting to resend confirmation email for:', email);
      
      // Check for referral code in URL to preserve it in resent email
      const urlParams = new URLSearchParams(window.location.search);
      const referralCode = urlParams.get('ref');
      
      const { data, error } = await supabase.functions.invoke('resend-confirmation', {
        body: { email, referralCode }
      });

      if (error) {
        console.error('Error resending confirmation email:', error);
        toast.error('Fehler beim Versenden der Bestätigungs-E-Mail');
        return;
      }

      console.log('Confirmation email resent successfully:', data);
      toast.success('Bestätigungs-E-Mail wurde erneut gesendet!');
    } catch (error) {
      console.error('Error calling resend-confirmation function:', error);
      toast.error('Fehler beim Versenden der Bestätigungs-E-Mail');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8 text-center">
      <div className="mb-6">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {t('register.success.title')}
        </h1>
        <p className="text-lg text-gray-600">
          {t('register.success.subtitle')}
        </p>
      </div>
      
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <Mail className="w-6 h-6 text-blue-600 mx-auto mb-2" />
        <p className="text-sm text-gray-700 mb-3">
          {t('register.success.instructions', { email })}
        </p>
        <p className="text-xs text-gray-500">
          {t('register.success.checkSpam')}
        </p>
      </div>

      <div className="space-y-3">
        <Button 
          onClick={handleResendEmail}
          disabled={isResending}
          variant="outline" 
          className="w-full"
        >
          {isResending ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              E-Mail wird gesendet...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Bestätigungs-E-Mail erneut senden
            </>
          )}
        </Button>
        
        <Button asChild variant="outline" className="w-full">
          <Link to="/login">
            {t('register.success.backToLogin')}
          </Link>
        </Button>
      </div>
    </div>
  );
};
